import React, {useState, useEffect} from 'react';
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	TextField,
	Grid,
	CircularProgress,
	Typography,
	IconButton,
	makeStyles,
	Box,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Select,
	MenuItem,
	Checkbox,
	FormControlLabel,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import SelectAndSearchComponent from '../../../../components/SelectAndSearchComponent/SelectAndSearchComponent';
import CreatableSelectComponent from '../../../../components/SelectAndSearchComponent/CreatableSelectComponent';
import API from '../../../../network/dashboardAPI';
import useAlert from '../../../../services/useAlert';
import {fetchSupplierProductBySupplierID} from '../../SupplierProduct/services/supplierProductService';

const useStyles = makeStyles((theme) => ({
	dialogPaper: {
		minHeight: '95vh',
		maxHeight: '95vh',
		width: '98vw',
		maxWidth: '1600px',
	},
	dialogTitle: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		padding: theme.spacing(2, 3),
		borderBottom: `1px solid ${theme.palette.divider}`,
		backgroundColor: theme.palette.background.default,
	},
	titleText: {
		fontWeight: 700,
		fontSize: '1.3rem',
		letterSpacing: '0.5px',
	},
	closeButton: {
		color: theme.palette.grey[500],
	},
	dialogContent: {
		padding: theme.spacing(3),
		overflow: 'auto',
	},
	sectionCard: {
		padding: theme.spacing(2.5),
		marginBottom: theme.spacing(2.5),
		border: '1px solid #e8edf2',
	},
	sectionTitle: {
		fontSize: '1rem',
		fontWeight: 700,
		marginBottom: theme.spacing(2),
		color: '#2d3748',
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(1),
		paddingBottom: theme.spacing(1.5),
		borderBottom: '2px solid #e8edf2',
		textTransform: 'uppercase',
		letterSpacing: '0.5px',
	},
	requiredField: {
		'& label': {
			fontWeight: 500,
			'&::after': {
				content: '" *"',
				color: theme.palette.error.main,
			},
		},
	},
	tableContainer: {
		border: '1px solid #e8edf2',
	},
	tableHeader: {
		backgroundColor: '#f8fafc',
		'& th': {
			fontWeight: 700,
			fontSize: '0.85rem',
			padding: theme.spacing(2),
			color: '#4a5568',
			textTransform: 'uppercase',
			letterSpacing: '0.5px',
			borderBottom: '2px solid #e8edf2',
		},
	},
	tableCell: {
		padding: theme.spacing(1.5),
		fontSize: '0.9rem',
		borderBottom: '1px solid #f1f3f5',
	},
	addButton: {
		marginTop: theme.spacing(2),
		textTransform: 'none',
		padding: theme.spacing(1, 2),
		fontWeight: 600,
	},
	dialogActions: {
		padding: theme.spacing(2, 3),
		borderTop: `1px solid ${theme.palette.divider}`,
		gap: theme.spacing(2),
	},
	submitButton: {
		fontWeight: 700,
		padding: theme.spacing(1.5, 4),
		textTransform: 'none',
		fontSize: '1rem',
	},
	cancelButton: {
		fontWeight: 600,
		padding: theme.spacing(1.5, 3),
		textTransform: 'none',
		fontSize: '1rem',
	},
	fieldLabel: {
		marginBottom: theme.spacing(1),
		fontSize: '0.9rem',
		fontWeight: 600,
		color: '#4a5568',
	},
	textField: {
		'& .MuiInputLabel-root': {
			fontWeight: 500,
		},
	},
	loadingContainer: {
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'center',
		alignItems: 'center',
		minHeight: '400px',
		gap: theme.spacing(3),
	},
	loadingText: {
		color: '#718096',
		fontWeight: 500,
		fontSize: '1.1rem',
	},
}));

const QuickProductCreateModal = ({
	                                 open,
	                                 onClose,
	                                 onProductCreated,
                                 }) => {
	const classes = useStyles();
	const alert = useAlert();

	const [loading, setLoading] = useState(false);
	const [categories, setCategories] = useState([]);
	const [subCategories, setSubCategories] = useState([]);
	const [countries, setCountries] = useState([]);
	const [brands, setBrands] = useState([]);
	const [suppliers, setSuppliers] = useState([]);
	const [loadingData, setLoadingData] = useState(true);

	const [formData, setFormData] = useState({
		name: '',
		product_code: '',
		cat_id: null,
		sub_cat_id: null,
		country_id: null,
		brand_id: null,
		price: '',
		variant_name: '',
		is_available: false,
	});

	// Supplier Products section
	const [supplierSections, setSupplierSections] = useState([{
		id: 1,
		supplier: null,
		supplier_obj: null,
		quantity: '',
		unit: '',
		productNumber: '',
		productNumberSet: null,
		productName: '',
		productNameSet: null,
		retailPrice: '',
		unitCost: '',
		isEnabled: true,
		comment: '',
	}]);

	const [errors, setErrors] = useState({});

	// Supplier product maps for auto-complete
	const [supplierProductsMap, setSupplierProductsMap] = useState({});
	const [supplierProductNumberMap, setSupplierProductNumberMap] = useState({});
	const [supplierProductNameMap, setSupplierProductNameMap] = useState({});
	const [isLoadingProducts, setIsLoadingProducts] = useState(false);

	// Fetch required data when modal opens
	useEffect(() => {
		if (open) {
			fetchRequiredData();
		}
	}, [open]);

	const fetchRequiredData = async () => {
		setLoadingData(true);
		try {
			// Fetch categories
			const catResponse = await API.getCategory({store_id: 1, get_type: 1});
			const catData = await catResponse.json();
			if (catData.success) {
				setCategories(catData.data || []);
			}

			// Fetch countries
			const countryResponse = await API.getCountries({});
			const countryData = await countryResponse.json();
			if (countryData.success) {
				setCountries(countryData.data || []);
			}

			// Fetch brands
			const brandResponse = await API.getBrands({});
			const brandData = await brandResponse.json();
			if (brandData.success) {
				setBrands(brandData.data || []);
			}

			// Fetch suppliers
			const supplierResponse = await API.getSuppliers({});
			const supplierData = await supplierResponse.json();
			if (supplierData.success) {
				setSuppliers(supplierData.data || []);
			}
		} catch (error) {
			alert.error('Error', 'Failed to load form data');
		} finally {
			setLoadingData(false);
		}
	};

	const handleAddSupplierSection = () => {
		setSupplierSections([
			...supplierSections,
			{
				id: supplierSections.length + 1,
				supplier: null,
				supplier_obj: null,
				quantity: '',
				unit: '',
				productNumber: '',
				productNumberSet: null,
				productName: '',
				productNameSet: null,
				retailPrice: '',
				unitCost: '',
				isEnabled: true,
				comment: '',
			}
		]);
	};

	const handleRemoveSupplierSection = (id) => {
		setSupplierSections(supplierSections.filter(section => section.id !== id));
	};

	const updateSupplierData = async (id, field, value) => {
		// Handle product number or product name selection (auto-fill)
		if ((field === 'productNumber' || field === 'productName') && value) {
			const productsArray = supplierProductsMap[id] || [];
			const product = productsArray.find((p) => (p.supplier_product_number === value.value || p.supplier_product_name === value.value));

			if (product) {
				// Auto-fill all product fields
				setSupplierSections(supplierSections.map(section => {
					if (section.id !== id) return section;
					return {
						...section,
						productNumber: product.supplier_product_number,
						productNumberSet: {value: product.supplier_product_number, label: product.supplier_product_number},
						productName: product.supplier_product_name,
						productNameSet: {value: product.supplier_product_name, label: product.supplier_product_name},
						retailPrice: product.retail_price,
						unitCost: product.unit_cost,
						unit: product.unit,
						comment: product.comment,
					};
				}));
				return;
			}
		}

		setSupplierSections(supplierSections.map(section => {
			if (section.id !== id) return section;

			// When supplier is selected, store the store_user_id and fetch products
			if (field === 'supplier') {
				// Fetch supplier products
				if (value && value.store_user_id) {
					fetchSupplierProducts(id, value.store_user_id);
				}

				return {
					...section,
					supplier: value ? value.store_user_id : null,
					supplier_obj: value || null, // Keep full object for display
					productNumber: null,
					productNumberSet: null,
					productName: null,
					productNameSet: null,
				};
			}

			// Handle product number/name changes
			if (field === 'productNumber') {
				return {
					...section,
					productNumber: value ? value.value : null,
					productNumberSet: value || null,
				};
			}

			if (field === 'productName') {
				return {
					...section,
					productName: value ? value.value : null,
					productNameSet: value || null,
				};
			}

			return {...section, [field]: value};
		}));
	};

	const fetchSupplierProducts = async (sectionId, supplierId) => {
		try {
			setIsLoadingProducts(true);
			const response = await fetchSupplierProductBySupplierID(supplierId);

			setSupplierProductsMap((prev) => ({...prev, [sectionId]: response.data || []}));

			const productNumbers = (response.data || []).map(item => ({
				value: item.supplier_product_number,
				label: item.supplier_product_number,
			}));
			const productNames = (response.data || []).map(item => ({
				value: item.supplier_product_name,
				label: item.supplier_product_name,
			}));

			setSupplierProductNumberMap((prev) => ({...prev, [sectionId]: productNumbers}));
			setSupplierProductNameMap((prev) => ({...prev, [sectionId]: productNames}));
		} catch (error) {
			console.error('Error fetching supplier products:', error);
			setSupplierProductsMap((prev) => ({...prev, [sectionId]: []}));
			setSupplierProductNumberMap((prev) => ({...prev, [sectionId]: []}));
			setSupplierProductNameMap((prev) => ({...prev, [sectionId]: []}));
		} finally {
			setIsLoadingProducts(false);
		}
	};

	const handleCategoryChange = async (catId) => {
		setFormData({...formData, cat_id: catId, sub_cat_id: null});

		// Fetch subcategories for selected category
		if (catId) {
			try {
				const response = await API.getSubCategoryMap({cat_id: catId});
				const data = await response.json();
				if (data.success) {
					setSubCategories(data.data || []);
				}
			} catch (error) {
				alert.error('Error', 'Failed to load subcategories');
			}
		} else {
			setSubCategories([]);
		}
	};

	const validateForm = () => {
		const newErrors = {};

		if (!formData.name) newErrors.name = 'Product name is required';
		if (!formData.product_code) newErrors.product_code = 'Product code is required';
		if (!formData.cat_id) newErrors.cat_id = 'Category is required';
		if (!formData.sub_cat_id) newErrors.sub_cat_id = 'Sub category is required';
		if (!formData.country_id) newErrors.country_id = 'Country is required';
		if (!formData.price || parseFloat(formData.price) <= 0) {
			newErrors.price = 'Valid price is required';
		}
		if (!formData.variant_name) newErrors.variant_name = 'Variant name is required';

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleSubmit = async () => {
		if (!validateForm()) {
			alert.error('Validation Error', 'Please fill in all required fields');
			return;
		}

		setLoading(true);
		try {
			// Collect supplier data matching ProductManagement format
			const supplierData = supplierSections
				.filter(section => section.supplier !== null)
				.map((section, index) => ({
					id: null,
					supplierId: section.supplier,
					quantity: parseFloat(section.quantity) || null,
					productNumber: section.productNumber || null,
					productName: section.productName || null,
					comment: section.comment || null,
					unit: section.unit || '',
					retailPrice: parseFloat(section.retailPrice) || null,
					unitCost: parseFloat(section.unitCost) || null,
					promotionPrice: null,
					promotionPriceDate: null,
					order: index + 1,
					isEnabled: section.isEnabled !== undefined ? section.isEnabled : true,
				}));

			const productData = {
				name: formData.name,
				product_code: formData.product_code,
				cat_id: formData.cat_id,
				sub_cat_id: formData.sub_cat_id,
				brand_id: formData.brand_id || null,
				country_id: formData.country_id,
				store_id: 1,
				image: '',
				name_km: '',
				about_item: '',
				how_to_use: '',
				ingredients: '',
				prices: [
					{
						price_id: 0,
						store_id: 1,
						name: formData.variant_name,
						price: parseFloat(formData.price),
						mrp: parseFloat(formData.price),
						is_available: formData.is_available,
						available_quantity: 0,
						weight_unit: 0,
					},
				],
				max_quantity_to_be_added: 1000000,
				same_day: false,
				suppliers: supplierData,
				promo: false,
				promo_price: null,
				promo_start_date: null,
				promo_end_date: null,
				new_arrival: false,
				arrival_limit: null,
				priority: false,
				trending: false,
				itemDietary: [],
				related_ids: [],
			};

			alert.loading('Creating Product...', 'Please wait while we create the product');

			const response = await API.addProduct(productData);
			const result = await response.json();

			alert.close();

			if (result.success) {
				alert.success('Product Created!', `${formData.name} has been created successfully`);

				// Call the callback with the new product info
				if (onProductCreated) {
					onProductCreated({
						item_id: result.item_id,
						name: formData.name,
						price: parseFloat(formData.price),
						code: formData.product_code,
					});
				}

				// Reset form
				setFormData({
					name: '',
					product_code: '',
					cat_id: null,
					sub_cat_id: null,
					country_id: null,
					brand_id: null,
					price: '',
					variant_name: '',
					is_available: false,
				});
				setSupplierSections([{
					id: 1,
					supplier: null,
					supplier_obj: null,
					quantity: '',
					unit: '',
					productNumber: '',
					productNumberSet: null,
					productName: '',
					productNameSet: null,
					retailPrice: '',
					unitCost: '',
					isEnabled: true,
					comment: '',
				}]);
				setErrors({});

				onClose();
			} else {
				alert.error('Creation Failed', result.message || 'Could not create the product. Please try again.');
			}
		} catch (error) {
			alert.close();
			alert.error('Error', error.message || 'Failed to create product. Please try again.');
		} finally {
			setLoading(false);
		}
	};

	const handleClose = () => {
		if (!loading) {
			setFormData({
				name: '',
				product_code: '',
				cat_id: null,
				sub_cat_id: null,
				country_id: null,
				brand_id: null,
				price: '',
				variant_name: '',
				is_available: false,
			});
			setSupplierSections([{
				id: 1,
				supplier: null,
				supplier_obj: null,
				quantity: '',
				unit: '',
				productNumber: '',
				productNumberSet: null,
				productName: '',
				productNameSet: null,
				retailPrice: '',
				unitCost: '',
				isEnabled: true,
				comment: '',
			}]);
			setErrors({});
			setSubCategories([]);
			onClose();
		}
	};

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			maxWidth={false}
			classes={{paper: classes.dialogPaper}}
		>
			<DialogTitle disableTypography className={classes.dialogTitle}>
				<Typography className={classes.titleText}>Create New Product</Typography>
				<IconButton
					aria-label="close"
					className={classes.closeButton}
					onClick={handleClose}
					disabled={loading}
				>
					<CloseIcon/>
				</IconButton>
			</DialogTitle>

			<DialogContent className={classes.dialogContent}>
				{loadingData ? (
					<div className={classes.loadingContainer}>
						<CircularProgress size={60}/>
						<Typography className={classes.loadingText}>
							Loading form data...
						</Typography>
					</div>
				) : (
					<Box>
						{/* Basic Information Section */}
						<Box className={classes.sectionCard}>
							<Typography className={classes.sectionTitle}>
								Basic Information
							</Typography>
							<Grid container spacing={2}>
								{/* Product Name */}
								<Grid item xs={12}>
									<TextField
										fullWidth
										label="Product Name"
										value={formData.name}
										onChange={(e) => setFormData({...formData, name: e.target.value})}
										error={!!errors.name}
										helperText={errors.name}
										className={`${classes.requiredField} ${classes.textField}`}
										disabled={loading}
										variant="outlined"
										placeholder="Enter product name"
										size="small"
									/>
								</Grid>

								{/* Product Code */}
								<Grid item xs={12} md={4}>
									<TextField
										fullWidth
										label="Product Code"
										value={formData.product_code}
										onChange={(e) => setFormData({...formData, product_code: e.target.value})}
										error={!!errors.product_code}
										helperText={errors.product_code}
										className={`${classes.requiredField} ${classes.textField}`}
										disabled={loading}
										variant="outlined"
										placeholder="e.g., PRD-001"
										size="small"
									/>
								</Grid>

								{/* Variant Name */}
								<Grid item xs={12} md={4}>
									<TextField
										fullWidth
										label="Variant Name"
										value={formData.variant_name}
										onChange={(e) => setFormData({...formData, variant_name: e.target.value})}
										error={!!errors.variant_name}
										helperText={errors.variant_name || 'e.g., 500ml, 1kg, Small'}
										className={`${classes.requiredField} ${classes.textField}`}
										disabled={loading}
										variant="outlined"
										placeholder="Default"
										size="small"
									/>
								</Grid>

								{/* Price */}
								<Grid item xs={12} md={4}>
									<TextField
										fullWidth
										label="Price ($)"
										type="number"
										value={formData.price}
										onChange={(e) => setFormData({...formData, price: e.target.value})}
										error={!!errors.price}
										helperText={errors.price}
										inputProps={{min: 0, step: 0.01}}
										className={`${classes.requiredField} ${classes.textField}`}
										disabled={loading}
										variant="outlined"
										placeholder="0.00"
										size="small"
									/>
								</Grid>

								{/* Product Status */}
								<Grid item xs={12}>
									<FormControlLabel
										control={
											<Checkbox
												checked={formData.is_available}
												onChange={(e) => setFormData({...formData, is_available: e.target.checked})}
												color="primary"
												disabled={loading}
											/>
										}
										label="Product Available (Enabled)"
									/>
								</Grid>
							</Grid>
						</Box>

						{/* Category Information Section */}
						<Box className={classes.sectionCard}>
							<Typography className={classes.sectionTitle}>
								Category & Classification
							</Typography>
							<Grid container spacing={2}>

								{/* Category */}
								<Grid item xs={12} md={6}>
									<Typography className={classes.fieldLabel}>Category *</Typography>
									<SelectAndSearchComponent
										placeholder="Select Category"
										options={categories}
										value={categories.find(c => c.cat_id === formData.cat_id) || null}
										onChange={(option) => handleCategoryChange(option?.cat_id || null)}
										disabled={loading}
										getOptionValue={(opt) => opt.list_categories?.[0]?.name}
										getOptionLabel={(cat) => (
											<div style={{display: "flex", alignItems: "center", gap: "8px"}}>
												<img src={cat?.list_categories?.[0]?.image}
												     style={{width: "24px", height: "24px", objectFit: "contain", borderRadius: "4px"}} alt=""/>
												<span style={{fontWeight: 500}}>{cat.list_categories?.[0]?.name}</span>
											</div>
										)}
									/>
									{errors.cat_id && (
										<Typography variant="caption" color="error" style={{marginTop: 4, display: 'block'}}>
											{errors.cat_id}
										</Typography>
									)}
								</Grid>

								{/* Sub Category */}
								<Grid item xs={12} md={6}>
									<Typography className={classes.fieldLabel}>Sub Category *</Typography>
									<SelectAndSearchComponent
										placeholder={formData.cat_id ? "Select SubCategory" : "Select Category first"}
										options={subCategories}
										value={subCategories.find(sc => sc.sub_cat_id === formData.sub_cat_id) || null}
										onChange={(option) => setFormData({...formData, sub_cat_id: option?.sub_cat_id || null})}
										disabled={loading || !formData.cat_id}
										getOptionValue={(opt) => opt.list_sub_categories?.[0]?.name}
										getOptionLabel={(opt) => (
											<div style={{display: "flex", alignItems: "center", gap: "8px"}}>
												<img
													src={opt?.list_sub_categories?.[0]?.image}
													style={{width: "24px", height: "24px", objectFit: "contain", borderRadius: "4px"}}
													alt=""
												/>
												<span style={{fontWeight: 500}}>{opt.list_sub_categories?.[0]?.name}</span>
											</div>
										)}
									/>
									{errors.sub_cat_id && (
										<Typography variant="caption" color="error" style={{marginTop: 4, display: 'block'}}>
											{errors.sub_cat_id}
										</Typography>
									)}
								</Grid>

								{/* Brand */}
								<Grid item xs={12} sm={6}>
									<Typography className={classes.fieldLabel}>Brand (Optional)</Typography>
									<SelectAndSearchComponent
										placeholder="Select Brand"
										options={brands}
										value={brands.find(b => b.brand_id === formData.brand_id) || null}
										onChange={(option) => setFormData({...formData, brand_id: option?.brand_id || null})}
										disabled={loading}
									/>
								</Grid>

								{/* Country */}
								<Grid item xs={12} sm={6}>
									<Typography className={classes.fieldLabel}>Country *</Typography>
									<SelectAndSearchComponent
										placeholder="Select Country"
										options={countries}
										value={countries.find(c => c.id === formData.country_id) || null}
										onChange={(option) => setFormData({...formData, country_id: option?.id || null})}
										disabled={loading}
										getOptionValue={(opt) => opt.name}
									/>
									{errors.country_id && (
										<Typography variant="caption" color="error" style={{marginTop: 4, display: 'block'}}>
											{errors.country_id}
										</Typography>
									)}
								</Grid>
							</Grid>
						</Box>

						{/* Supplier Products Section */}
						<Box className={classes.sectionCard}>
							<Typography className={classes.sectionTitle}>
								Supplier Products
							</Typography>
							<Box className={classes.tableContainer}>
								<Table size="small">
									<TableHead className={classes.tableHeader}>
										<TableRow>
											<TableCell>Supplier</TableCell>
											<TableCell>Product #</TableCell>
											<TableCell>Product Name</TableCell>
											<TableCell>Quantity</TableCell>
											<TableCell>Unit</TableCell>
											<TableCell>Retail Price</TableCell>
											<TableCell>Unit Cost</TableCell>
											<TableCell>Status</TableCell>
											<TableCell width={60}>Action</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{supplierSections.map((section) => (
											<TableRow key={section.id}>
												<TableCell className={classes.tableCell} style={{minWidth: 200}}>
													<SelectAndSearchComponent
														placeholder="Select Supplier"
														options={suppliers}
														value={section.supplier_obj || null}
														onChange={(option) => updateSupplierData(section.id, 'supplier', option)}
														disabled={loading}
														getOptionValue={(opt) => opt.store_users?.[0]?.first_name}
														getOptionLabel={(opt) => (
															<div style={{display: 'flex', alignItems: 'center', gap: '5px', fontSize: '14px'}}>
																{opt.store_users?.[0]?.first_name}
															</div>
														)}
														menuPortalTarget={document.body}
														styles={{
															control: (base) => ({...base, fontSize: '14px', minHeight: '40px', minWidth: 200}),
															menu: (base) => ({...base, fontSize: '14px'}),
															menuPortal: (base) => ({...base, zIndex: 9999}),
														}}
													/>
												</TableCell>
												<TableCell className={classes.tableCell}>
													<CreatableSelectComponent
														options={supplierProductNumberMap[section.id] || []}
														value={section.productNumberSet}
														onChange={(opt) => updateSupplierData(section.id, 'productNumber', opt)}
														placeholder="Product #"
														isDisabled={loading || !section.supplier}
														isLoading={isLoadingProducts}
														isClearable
														menuPortalTarget={document.body}
														styles={{
															control: (base) => ({...base, fontSize: '14px', minHeight: '40px', minWidth: 180}),
															menu: (base) => ({...base, fontSize: '14px'}),
															menuPortal: (base) => ({...base, zIndex: 9999}),
														}}
													/>
												</TableCell>
												<TableCell className={classes.tableCell}>
													<CreatableSelectComponent
														options={supplierProductNameMap[section.id] || []}
														value={section.productNameSet}
														onChange={(opt) => updateSupplierData(section.id, 'productName', opt)}
														placeholder="Product Name"
														isDisabled={loading || !section.supplier}
														isLoading={isLoadingProducts}
														isClearable
														menuPortalTarget={document.body}
														styles={{
															control: (base) => ({...base, fontSize: '14px', minHeight: '40px', minWidth: 250}),
															menu: (base) => ({...base, fontSize: '14px'}),
															menuPortal: (base) => ({...base, zIndex: 9999}),
														}}
													/>
												</TableCell>
												<TableCell className={classes.tableCell}>
													<TextField
														type="number"
														placeholder="0.00"
														value={section.quantity}
														onChange={(e) => updateSupplierData(section.id, 'quantity', e.target.value)}
														disabled={loading}
														variant="outlined"
														size="small"
														inputProps={{min: 0, step: 0.1, style: {fontSize: '14px'}}}
														style={{width: 130}}
													/>
												</TableCell>
												<TableCell className={classes.tableCell}>
													<Select
														value={section.unit}
														onChange={(e) => updateSupplierData(section.id, 'unit', e.target.value)}
														disabled={loading}
														displayEmpty
														variant="outlined"
														size="small"
														style={{fontSize: '14px', minWidth: 130, height: '40px'}}
													>
														<MenuItem value="">Unit</MenuItem>
														<MenuItem value="pcs">pcs</MenuItem>
														<MenuItem value="kg">kg</MenuItem>
														<MenuItem value="g">g</MenuItem>
														<MenuItem value="mg">mg</MenuItem>
														<MenuItem value="l">l</MenuItem>
														<MenuItem value="ml">ml</MenuItem>
													</Select>
												</TableCell>
												<TableCell className={classes.tableCell}>
													<TextField
														type="number"
														placeholder="0.00"
														value={section.retailPrice}
														onChange={(e) => updateSupplierData(section.id, 'retailPrice', e.target.value)}
														disabled={loading}
														variant="outlined"
														size="small"
														inputProps={{min: 0, step: 0.01, style: {fontSize: '14px'}}}
														style={{width: 130}}
													/>
												</TableCell>
												<TableCell className={classes.tableCell}>
													<TextField
														type="number"
														placeholder="0.00"
														value={section.unitCost}
														onChange={(e) => updateSupplierData(section.id, 'unitCost', e.target.value)}
														disabled={loading}
														variant="outlined"
														size="small"
														inputProps={{min: 0, step: 0.01, style: {fontSize: '14px'}}}
														style={{width: 130}}
													/>
												</TableCell>
												<TableCell className={classes.tableCell}>
													<Checkbox
														checked={section.isEnabled !== undefined ? section.isEnabled : true}
														onChange={(e) => updateSupplierData(section.id, 'isEnabled', e.target.checked)}
														color="primary"
														disabled={loading}
														size="small"
													/>
												</TableCell>
												<TableCell className={classes.tableCell}>
													<IconButton
														size="small"
														onClick={() => handleRemoveSupplierSection(section.id)}
														disabled={loading || supplierSections.length === 1}
														style={{color: '#e53e3e'}}
													>
														<DeleteIcon fontSize="small"/>
													</IconButton>
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</Box>
							<Button
								className={classes.addButton}
								startIcon={<AddIcon/>}
								onClick={handleAddSupplierSection}
								disabled={loading}
							>
								Add Another Supplier
							</Button>
						</Box>
					</Box>
				)}
			</DialogContent>

			<DialogActions className={classes.dialogActions}>
				<Button
					onClick={handleClose}
					disabled={loading}
				>
					Cancel
				</Button>
				<Button
					onClick={handleSubmit}
					color="primary"
					variant="contained"
					startIcon={loading ? <CircularProgress size={20} color="inherit"/> : <AddIcon/>}
					disabled={loading || loadingData}
				>
					{loading ? 'Creating Product...' : 'Create Product'}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default QuickProductCreateModal;
